import { ChevronUp } from "lucide-react";

import { airedFraction } from "../api/polling.js";
import { useNow } from "../lib/use-now.js";
import { Artwork, LiveBars } from "./artwork.js";
import { usePlayer } from "./player-provider.js";
import { PlayButton } from "./transport.js";

/**
 * The mobile player, docked directly above the tab bar: artwork, what is airing,
 * play or stop. Everything else about it lives one tap away in the sheet, so
 * the dock stays a thumb-height strip that never competes with the page above it.
 */
export function MiniPlayer({ onExpand }: { onExpand: () => void }) {
  const { detail, live, state, stop, target, toggle } = usePlayer();
  const airing = live.data?.nowPlaying ?? null;
  const now = useNow(1000, state === "playing" || state === "stalled");

  if (!target) return null;

  const fraction = airedFraction(live.data, now);

  return (
    <div className="border-t border-border bg-surface/97 backdrop-blur lg:hidden">
      <div
        aria-hidden="true"
        className="h-0.5 w-full bg-border/60"
        style={{
          background: `linear-gradient(to right, var(--accent) ${String(fraction * 100)}%, transparent ${String(fraction * 100)}%)`,
        }}
      />
      <div className="flex h-(--mini-player-h) items-center gap-3 pr-3 pl-3">
        <button
          aria-label="Open the now playing view"
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          onClick={onExpand}
          type="button"
        >
          <Artwork airing={airing} className="size-11" />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              {state === "playing" ? (
                <LiveBars className="h-2.5 shrink-0 text-accent-text" />
              ) : null}
              <span className="truncate text-sm font-semibold text-ink">
                {airing?.title ?? target.feedName}
              </span>
            </span>
            <span className="mt-0.5 block truncate text-xs text-ink-3">
              {state === "blocked"
                ? "Blocked: HTTPS page, HTTP stream"
                : state === "error"
                  ? (detail ?? "The stream could not be played.")
                  : state === "connecting"
                    ? "Connecting…"
                    : (airing?.artist ?? "") || target.feedName}
            </span>
          </span>
          <ChevronUp
            aria-hidden="true"
            className="size-4 shrink-0 text-ink-3"
          />
        </button>
        <PlayButton
          feedName={target.feedName}
          onActivate={() => {
            if (state === "idle" || state === "error" || state === "blocked") {
              toggle(target);
            } else stop();
          }}
          state={state}
        />
      </div>
    </div>
  );
}
